// The arena renderer (lane C; spec §11, territory-visuals.md). Draw order per frame:
//  1 outside: a world-y gradient (ARENA_CLIENT.colors.outsideTop → outsideBottom over the grid);
//    the rim lip (the arena disc drawn 1 cell lower in rimLip) and the ground disc;
//  2 land SIDE pass: chunk bitmaps at ground level in a layer, darkened to V × sideShade;
//  3 land TOP pass: chunk bitmaps lifted liftCells, with the grooves of trails crossing enemy land
//    punched out (destination-out), so the side shade shows in the groove with a darker lip;
//  4 the capture flash; 5 trails: one stroked polyline per player along the path its head was
//    drawn on since it left its land (trail-track.ts; the trail cell centres, straightened and
//    smoothed, only for a part never seen drawn), main colour α trailAlpha, trailDrawCells wide,
//    round caps and joins, at ground level; 6 drops (coins with their value, popping in);
//  7 heads (D-V1): a rounded block headDrawCells a side rotating with the heading — the base in
//    the base shade at ground level, the top lifted liftCells in the main colour with a 10 % rim
//    in the side shade; the shield's marching dashed ring, the cash-out ring, the bounty glow;
//  8 labels: name (Ubuntu Bold 24 px × s; an empty name shows our default name, as on the
//    board), bag (18 px × s, money green; silver in practice), crown on the board's #1, the `$`
//    marker while cashing out, a plug on autopilot;
//  9 effects: break-up particles, chewing chips, floating labels over the own head (plain fill,
//    no shadow, like the reference's capture label).
// Skins (arena/skins.ts, SK-2/SK-3): the land paint is LandLayer.painter (skinLandPainter: the
// pattern over the land top, tiles cached per skin, colour and px); `drawTrail` tints a skin's
// trail; `drawHead` adds the skin's decoration on the block's face. "Hide skins" turns all three
// off (`hideSkins`, which redraws the land once when it changes).
// The own head comes from the predictor; everyone else is interpolated at the render tick.
// The classic FFA renderer (render/renderer.ts) is untouched.

import { ARENA_CLIENT, ARENA_NET, arenaCircle, arenaSkinById, type Rgb } from '@ballio/shared';
import { type Canvas2D, type Ctx2D, createCanvas, get2d, sizeCanvas } from '../render/canvas.js';
import { css, cssAlpha } from '../render/theme.js';
import { t as tr } from '../ui/i18n/t.js';
import type { ArenaView } from './camera.js';
import { type ArenaEffects, labelAlpha, labelDrift } from './effects.js';
import { ArenaLabels, screenScale } from './labels.js';
import { BLOCK_FACE, drawBlock } from './block.js';
import { cellSetContours, pathPolygons, type Polygons } from './contour.js';
import { LandLayer } from './land-layer.js';
import { shadesOf } from './shades.js';
import { drawSkinHead, SkinTileCache, skinLandPainter, skinTrailColor } from './skins.js';
import type { ArenaStore, HeadSample } from './store.js';
import { TrailTracks } from './trail-track.js';

const TAU = Math.PI * 2;
/** Practice values are silver (money-mode §1.4). */
export const PRACTICE_SILVER: Rgb = 0xd5dbe3;
const COIN_FILL: Rgb = 0xffcf3f;
const COIN_RIM: Rgb = 0xc98a00;

export interface ArenaDrawHead {
  readonly playerId: number;
  readonly x: number;
  readonly y: number;
  readonly heading: number;
}

export interface ArenaDrawOptions {
  readonly view: ArenaView;
  /** Device px per CSS px (DPR × quality scale). */
  readonly k: number;
  readonly nowMs: number;
  /** Fractional render tick (others' heads). */
  readonly renderTick: number;
  /** The predicted own head (null = not playing, or no server head yet). */
  readonly ownHead: ArenaDrawHead | null;
  /** A lamport value as label text ("$12.40"), or null to hide bag labels. */
  readonly formatBag: (lamports: number) => string | null;
  readonly practice: boolean;
  /** The board's #1 (crown), 0 = none. */
  readonly crownId: number;
  readonly hideNames: boolean;
  /** "Hide skins": plain blocks, flat land, main-colour trails (default false). */
  readonly hideSkins?: boolean;
  /** The own pointer target (radians): what a 'pointer' skin decoration faces (default: heading). */
  readonly ownAim?: number | null;
  /** Draw nothing but the scene (menu backdrop dims over it). */
  readonly effects: ArenaEffects | null;
}

/** Chaikin corner cutting on a flat [x, y, …] polyline (endpoints kept). */
export function chaikin(points: readonly number[], passes = 2): number[] {
  let p = points.slice();
  for (let n = 0; n < passes; n++) {
    if (p.length < 6) return p;
    const out: number[] = [p[0] as number, p[1] as number];
    for (let i = 0; i + 3 < p.length; i += 2) {
      const ax = p[i] as number;
      const ay = p[i + 1] as number;
      const bx = p[i + 2] as number;
      const by = p[i + 3] as number;
      out.push(
        ax * 0.75 + bx * 0.25,
        ay * 0.75 + by * 0.25,
        ax * 0.25 + bx * 0.75,
        ay * 0.25 + by * 0.75,
      );
    }
    out.push(p[p.length - 2] as number, p[p.length - 1] as number);
    p = out;
  }
  return p;
}

/** Ramer–Douglas–Peucker on a flat [x, y, …] polyline: drops points within `tol` of the chord. */
export function simplifyPolyline(points: readonly number[], tol: number): number[] {
  const n = points.length / 2;
  if (n <= 2) return points.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length > 0) {
    const [a, b] = stack.pop() as [number, number];
    const ax = points[a * 2] as number;
    const ay = points[a * 2 + 1] as number;
    const dx = (points[b * 2] as number) - ax;
    const dy = (points[b * 2 + 1] as number) - ay;
    const len = Math.hypot(dx, dy) || 1;
    let worst = -1;
    let worstD = tol;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs(
        (dx * ((points[i * 2 + 1] as number) - ay) - dy * ((points[i * 2] as number) - ax)) / len,
      );
      if (d > worstD) {
        worstD = d;
        worst = i;
      }
    }
    if (worst >= 0) {
      keep[worst] = 1;
      stack.push([a, worst], [worst, b]);
    }
  }
  const out: number[] = [];
  for (let i = 0; i < n; i++)
    if (keep[i]) out.push(points[i * 2] as number, points[i * 2 + 1] as number);
  return out;
}

/** Cell-centre staircases within this many cells of a straight line are drawn straight. */
export const TRAIL_SIMPLIFY_CELLS = 0.75;

/** Groove sampling step along a trail polyline segment (cells; visuals §3, C-4). */
const GROOVE_STEP_CELLS = 0.5;
/**
 * Each punched groove run reaches this far past its first and last enemy sample (cells), so it
 * never stops short of a rounded enemy edge (the land is smoothed, ownership is per cell; OV-2).
 */
export const GROOVE_EXTEND_CELLS = 0.5;

/**
 * The trail polyline (cells) for trail cells `cells` ending at the rendered head: cell centres,
 * minus trailing cells already ahead of a head drawn behind the server (interpolation),
 * straightened (RDP, TRAIL_SIMPLIFY_CELLS) and smoothed (2 Chaikin passes), then the head.
 */
export function trailPolyline(
  cells: readonly number[],
  cols: number,
  head: { x: number; y: number; heading: number } | null,
): number[] {
  const pts: number[] = [];
  for (const c of cells) pts.push((c % cols) + 0.5, Math.floor(c / cols) + 0.5);
  if (head) {
    const fx = Math.cos(head.heading);
    const fy = Math.sin(head.heading);
    while (pts.length >= 2) {
      const dx = (pts[pts.length - 2] as number) - head.x;
      const dy = (pts[pts.length - 1] as number) - head.y;
      if (dx * fx + dy * fy <= 0) break;
      pts.length -= 2;
    }
  }
  const smooth = chaikin(simplifyPolyline(pts, TRAIL_SIMPLIFY_CELLS), 2);
  if (head) smooth.push(head.x, head.y);
  return smooth;
}

interface ShieldRing {
  polys: Polygons;
  /** chunkKey over the bounds when traced. */
  key: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** A hash of the chunk versions over a cell rectangle (changes when any of them changes). */
function chunkKey(st: ArenaStore, r: { x0: number; y0: number; x1: number; y1: number }): number {
  const n = ARENA_NET.chunkSize;
  let h = 17;
  for (let ky = Math.floor(r.y0 / n); ky <= Math.floor(r.y1 / n); ky++) {
    for (let kx = Math.floor(r.x0 / n); kx <= Math.floor(r.x1 / n); kx++) {
      h = (Math.imul(h, 31) + (st.chunkVersion[ky * st.chunksX + kx] ?? 0)) | 0;
    }
  }
  return h;
}

export class ArenaRenderer {
  readonly store: ArenaStore;
  readonly land: LandLayer;
  readonly labels = new ArenaLabels();
  /** Skin pattern tiles (SK-3). */
  readonly skinTiles = new SkinTileCache();
  private skinsOn = true;
  private side: { canvas: Canvas2D; ctx: Ctx2D } | null = null;
  private top: { canvas: Canvas2D; ctx: Ctx2D } | null = null;
  /** Each shielded player's land outline (the shield's dashed ring follows it). */
  private readonly shieldRings = new Map<number, ShieldRing>();
  /** Heads drawn last frame (the client's chip and view tests). */
  readonly drawnHeads: ArenaDrawHead[] = [];
  /** Each head's drawn path: trails follow it (visuals §3). */
  readonly trailTracks = new TrailTracks();

  constructor(store: ArenaStore) {
    this.store = store;
    this.land = new LandLayer(store);
    this.land.painter = skinLandPainter(this.skinTiles, () => this.skinsOn);
  }

  /** Shows or hides skins (the "Hide skins" setting); a change redraws the land. */
  setSkinsVisible(on: boolean): void {
    if (on === this.skinsOn) return;
    this.skinsOn = on;
    this.land.invalidate();
  }

  get skinsVisible(): boolean {
    return this.skinsOn;
  }

  private layer(
    which: 'side' | 'top',
    w: number,
    h: number,
  ): { canvas: Canvas2D; ctx: Ctx2D } | null {
    let l = which === 'side' ? this.side : this.top;
    if (!l) {
      try {
        const canvas = createCanvas(w, h);
        const ctx = get2d(canvas);
        if (!ctx) return null;
        l = { canvas, ctx };
      } catch {
        return null;
      }
      if (which === 'side') this.side = l;
      else this.top = l;
    }
    sizeCanvas(l.canvas, w, h);
    return l;
  }

  /** The heads to draw this frame: others interpolated at `rt`, the own one predicted. */
  collectHeads(rt: number, own: ArenaDrawHead | null): ArenaDrawHead[] {
    const out = this.drawnHeads;
    out.length = 0;
    const ownId = this.store.own.playerId;
    for (const hd of this.store.heads.values()) {
      if (hd.playerId === ownId && own) continue;
      const s: HeadSample | null = this.store.sampleHead(hd.playerId, rt);
      if (s) out.push({ playerId: hd.playerId, x: s.x, y: s.y, heading: s.heading });
    }
    if (own) out.push(own);
    out.sort((a, b) => a.y - b.y);
    return out;
  }

  /** Draws one frame into `ctx` (its canvas is CSS size × `o.k`). */
  draw(ctx: Ctx2D, o: ArenaDrawOptions): void {
    const { view, k } = o;
    const store = this.store;
    this.setSkinsVisible(o.hideSkins !== true);
    // The land, trails and drops drawn are those of the render tick (P-3; a no-op when the
    // client already advanced the store this frame).
    store.advance(o.renderTick);
    const W = view.width;
    const H = view.height;
    const s = view.scale;
    const ox = W / 2 - view.cx * s;
    const oy = H / 2 - view.cy * s;
    const lift = ARENA_CLIENT.liftCells * s;
    const ss = screenScale(W, H);
    const heads = this.collectHeads(o.renderTick, o.ownHead);
    const headOf = new Map<number, ArenaDrawHead>();
    for (const hd of heads) headOf.set(hd.playerId, hd);

    ctx.setTransform(k, 0, 0, k, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    // 1 Outside, rim lip, ground.
    const g = ctx.createLinearGradient(0, oy, 0, oy + store.rows * s);
    g.addColorStop(0, css(ARENA_CLIENT.colors.outsideTop));
    g.addColorStop(1, css(ARENA_CLIENT.colors.outsideBottom));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    this.drawGround(ctx, ox, oy, s);

    // 2–3 Land.
    this.land.prepare(view, k);
    const pw = Math.max(1, Math.round(W * k));
    const ph = Math.max(1, Math.round(H * k));
    const side = this.layer('side', pw, ph);
    const top = this.layer('top', pw, ph);
    const trails = this.trailPaths(heads);
    if (side && top) {
      side.ctx.setTransform(1, 0, 0, 1, 0, 0);
      side.ctx.globalCompositeOperation = 'source-over';
      side.ctx.clearRect(0, 0, pw, ph);
      side.ctx.setTransform(k, 0, 0, k, 0, 0);
      this.land.blit(side.ctx, view, 0);
      side.ctx.globalCompositeOperation = 'source-atop';
      side.ctx.fillStyle = cssAlpha(0x000000, 1 - ARENA_CLIENT.sideShade);
      side.ctx.fillRect(0, 0, W, H);
      side.ctx.globalCompositeOperation = 'source-over';

      top.ctx.setTransform(1, 0, 0, 1, 0, 0);
      top.ctx.globalCompositeOperation = 'source-over';
      top.ctx.clearRect(0, 0, pw, ph);
      top.ctx.setTransform(k, 0, 0, k, 0, 0);
      this.land.blit(top.ctx, view, -lift);
      this.punchGrooves(top.ctx, trails, ox, oy - lift, s);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(side.canvas as CanvasImageSource, 0, 0);
      ctx.drawImage(top.canvas as CanvasImageSource, 0, 0);
      ctx.setTransform(k, 0, 0, k, 0, 0);
    } else {
      this.land.blit(ctx, view, -lift);
    }

    // 4 Exit fades and the capture flash.
    o.effects?.drawFades(ctx, view, o.nowMs, lift);
    o.effects?.drawFlash(ctx, view, o.nowMs, lift);

    // 5 Trails.
    for (const t of trails) this.drawTrail(ctx, t, ox, oy, s);

    // 6 Drops.
    this.drawDrops(ctx, o, ox, oy, s, ss);

    // 7 Heads.
    for (const hd of heads) this.drawHead(ctx, hd, o, ox, oy, s);

    // 8 Labels.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    for (const hd of heads) this.drawHeadLabels(ctx, hd, o, ox, oy, s, ss);

    // 9 Effects.
    if (o.effects) {
      o.effects.drawParticles(ctx, view, o.nowMs);
      const own = o.ownHead;
      if (own) this.drawFloating(ctx, o, own, ox, oy, s, ss);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  /**
   * One player's trail (skin hook): the polyline through its trail cell centres to the rendered
   * head, main colour (or the skin's light tone) α trailAlpha, trailDrawCells wide, round caps
   * and joins, at ground level.
   */
  drawTrail(
    ctx: Ctx2D,
    t: { readonly playerId: number; readonly points: readonly number[] },
    ox: number,
    oy: number,
    s: number,
  ): void {
    if (t.points.length < 4) return;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = ARENA_CLIENT.trailDrawCells * s;
    const st = this.store;
    const color = skinTrailColor(st.skinOf(t.playerId), st.colorOf(t.playerId), this.skinsOn);
    ctx.strokeStyle = cssAlpha(color, ARENA_CLIENT.trailAlpha);
    ctx.beginPath();
    ctx.moveTo(ox + (t.points[0] as number) * s, oy + (t.points[1] as number) * s);
    for (let i = 2; i < t.points.length; i += 2) {
      ctx.lineTo(ox + (t.points[i] as number) * s, oy + (t.points[i + 1] as number) * s);
    }
    ctx.stroke();
  }

  private drawGround(ctx: Ctx2D, ox: number, oy: number, s: number): void {
    const st = this.store;
    const shape = { shape: st.info.shape, cols: st.cols, rows: st.rows };
    if (shape.shape === 'square') {
      ctx.fillStyle = css(ARENA_CLIENT.colors.rimLip);
      ctx.fillRect(ox, oy + s, st.cols * s, st.rows * s);
      ctx.fillStyle = css(ARENA_CLIENT.colors.ground);
      ctx.fillRect(ox, oy, st.cols * s, st.rows * s);
      return;
    }
    const c = arenaCircle(shape);
    const r = (c.r + 0.5) * s;
    ctx.fillStyle = css(ARENA_CLIENT.colors.rimLip);
    ctx.beginPath();
    ctx.arc(ox + c.cx * s, oy + (c.cy + 1) * s, r, 0, TAU);
    ctx.fill();
    ctx.fillStyle = css(ARENA_CLIENT.colors.ground);
    ctx.beginPath();
    ctx.arc(ox + c.cx * s, oy + c.cy * s, r, 0, TAU);
    ctx.fill();
  }

  private trailPaths(heads: readonly ArenaDrawHead[]): { playerId: number; points: number[] }[] {
    const out: { playerId: number; points: number[] }[] = [];
    const st = this.store;
    const byId = new Map<number, ArenaDrawHead>();
    for (const hd of heads) byId.set(hd.playerId, hd);
    this.trailTracks.update(heads, st);
    const fallback = (cells: readonly number[], head: ArenaDrawHead | null): number[] =>
      trailPolyline(cells, st.cols, head);
    for (const [id, tr] of st.trails) {
      if (tr.cells.length === 0) continue;
      const head = byId.get(id) ?? null;
      out.push({ playerId: id, points: this.trailTracks.polyline(id, tr.cells, head, fallback) });
    }
    return out;
  }

  /** Grooves: trails crossing ENEMY land cut the land top (visuals §3). */
  private punchGrooves(
    ctx: Ctx2D,
    trails: readonly { playerId: number; points: number[] }[],
    ox: number,
    oy: number,
    s: number,
  ): void {
    const st = this.store;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.strokeStyle = '#000';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = ARENA_CLIENT.trailDrawCells * s;
    // The polyline is simplified (a straight trail keeps only its ends), so ownership is sampled
    // along every segment at GROOVE_STEP_CELLS, not at the vertices: each enemy-land run is
    // punched from the sample before it to the first sample after it, each end pushed out by
    // GROOVE_EXTEND_CELLS along the trail (not into the trail owner's own land).
    for (const t of trails) {
      const p = t.points;
      if (p.length < 2) continue;
      const id = t.playerId;
      const enemyAt = (x: number, y: number): boolean => {
        const o = st.ownerAtPoint(x, y);
        return o !== 0 && o !== id;
      };
      // A run end pushed `d` cells along (dx, dy) from (x, y), unless that lands on own land.
      const push = (x: number, y: number, dx: number, dy: number): [number, number] => {
        const len = Math.hypot(dx, dy);
        if (!(len > 0)) return [x, y];
        const ex = x + (dx / len) * GROOVE_EXTEND_CELLS;
        const ey = y + (dy / len) * GROOVE_EXTEND_CELLS;
        return st.ownerAtPoint(ex, ey) === id ? [x, y] : [ex, ey];
      };
      let open = false;
      let px = p[0] as number;
      let py = p[1] as number;
      const begin = (x: number, y: number, dx: number, dy: number): void => {
        const [sx, sy] = push(x, y, -dx, -dy);
        ctx.beginPath();
        ctx.moveTo(ox + sx * s, oy + sy * s);
        open = true;
      };
      const end = (x: number, y: number, dx: number, dy: number): void => {
        const [ex, ey] = push(x, y, dx, dy);
        ctx.lineTo(ox + x * s, oy + y * s);
        ctx.lineTo(ox + ex * s, oy + ey * s);
        ctx.stroke();
        open = false;
      };
      if (enemyAt(px, py) && p.length >= 4) {
        begin(px, py, (p[2] as number) - px, (p[3] as number) - py);
      }
      for (let i = 2; i + 1 < p.length; i += 2) {
        const ax = px;
        const ay = py;
        const bx = p[i] as number;
        const by = p[i + 1] as number;
        const dx = bx - ax;
        const dy = by - ay;
        const n = Math.max(1, Math.ceil(Math.hypot(dx, dy) / GROOVE_STEP_CELLS));
        for (let k = 1; k <= n; k++) {
          const x = ax + (dx * k) / n;
          const y = ay + (dy * k) / n;
          const enemy = enemyAt(x, y);
          if (enemy && !open) {
            begin(px, py, dx, dy);
          } else if (!enemy && open) {
            end(x, y, dx, dy);
          } else if (enemy && k === n) {
            ctx.lineTo(ox + x * s, oy + y * s);
          }
          px = x;
          py = y;
        }
      }
      if (open) ctx.stroke();
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  private drawDrops(
    ctx: Ctx2D,
    o: ArenaDrawOptions,
    ox: number,
    oy: number,
    s: number,
    ss: number,
  ): void {
    const st = this.store;
    if (st.drops.size === 0) return;
    const { view } = o;
    const hw = view.width / 2 / s + 2;
    const hh = view.height / 2 / s + 2;
    let i = 0;
    for (const d of st.drops.values()) {
      const cx = (d.cell % st.cols) + 0.5;
      const cy = Math.floor(d.cell / st.cols) + 0.5;
      if (Math.abs(cx - view.cx) > hw || Math.abs(cy - view.cy) > hh) continue;
      // Pop-in: 0 → 1 in 160 ms easeOutBack, 30 ms stagger.
      const age = o.nowMs - d.atMs - (i++ % 8) * 30;
      const t = Math.min(1, Math.max(0, age / 160));
      const c1 = 1.70158;
      const pop = t >= 1 ? 1 : 1 + (c1 + 1) * (t - 1) ** 3 + c1 * (t - 1) ** 2;
      if (pop <= 0.01) continue;
      const x = ox + cx * s;
      const y = oy + cy * s;
      const r = 0.42 * s * pop;
      ctx.setTransform(o.k, 0, 0, o.k, 0, 0);
      ctx.fillStyle = css(COIN_RIM);
      ctx.beginPath();
      ctx.arc(x, y + 0.12 * s, r, 0, TAU);
      ctx.fill();
      ctx.fillStyle = css(COIN_FILL);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = css(COIN_RIM);
      ctx.lineWidth = Math.max(1, 0.06 * s);
      ctx.beginPath();
      ctx.arc(x, y, r * 0.72, 0, TAU);
      ctx.stroke();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.labels.draw(
        ctx,
        '$',
        x * o.k,
        (y + r * 0.42) * o.k,
        r * 1.2 * o.k,
        COIN_RIM,
        1,
        1,
        false,
      );
      const text = o.formatBag(d.value);
      if (text) {
        this.labels.draw(
          ctx,
          text,
          x * o.k,
          (y - r - 4 * ss) * o.k,
          14 * ss * o.k,
          o.practice ? PRACTICE_SILVER : ARENA_CLIENT.colors.bag,
          1,
        );
      }
    }
    ctx.setTransform(o.k, 0, 0, o.k, 0, 0);
  }

  /**
   * One head (skin hook): the owner's rounded block (D-V1) with its base, side rim and top, the
   * skin's decoration on the face, plus the bounty glow, shield ring and cash-out ring.
   */
  drawHead(
    ctx: Ctx2D,
    hd: ArenaDrawHead,
    o: ArenaDrawOptions,
    ox: number,
    oy: number,
    s: number,
  ): void {
    const st = this.store;
    const view = o.view;
    const x = ox + hd.x * s;
    const y = oy + hd.y * s;
    if (x < -3 * s || y < -3 * s || x > view.width + 3 * s || y > view.height + 3 * s) return;
    const sh = shadesOf(st.colorOf(hd.playerId));
    const side = ARENA_CLIENT.headDrawCells * s;
    const lift = ARENA_CLIENT.liftCells * s;
    const state = st.heads.get(hd.playerId);
    ctx.setTransform(o.k, 0, 0, o.k, 0, 0);

    // Bounty glow: a soft golden halo by bag on a log scale $1 … $500 (spec §11).
    const value = st.valueOf(hd.playerId);
    const usd = value !== null && st.solUsd > 0 ? (value / 1e9) * st.solUsd : 0;
    if (usd >= 1 && !o.practice) {
      const k = Math.min(1, Math.log(usd) / Math.log(500));
      const glow = ctx.createRadialGradient(x, y - lift, side * 0.3, x, y - lift, side * (1.2 + k));
      glow.addColorStop(0, cssAlpha(ARENA_CLIENT.colors.crownFill, 0.35 * k + 0.1));
      glow.addColorStop(1, cssAlpha(ARENA_CLIENT.colors.crownFill, 0));
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(x, y - lift, side * (1.2 + k), 0, TAU);
      ctx.fill();
    }

    // Base block at ground level, then the lifted top with its rim; the skin's decoration on it.
    const faceY = drawBlock(ctx, x, y, side, lift, hd.heading, sh);
    const skin = this.skinsOn ? arenaSkinById(st.skinOf(hd.playerId)) : null;
    if (skin) {
      const own = o.ownHead !== null && hd === o.ownHead;
      drawSkinHead(ctx, skin, {
        x,
        y: faceY,
        face: side * BLOCK_FACE,
        lift,
        heading: hd.heading,
        aim: own && o.ownAim != null ? o.ownAim : hd.heading,
        color: sh.main,
        nowMs: o.nowMs,
        seed: hd.playerId,
      });
    }

    if (state?.shielded) {
      // Marching dashed outline on the land's top edge (spec §6, visuals §11): the same
      // rounded outline the land is drawn with (the spawn blob's circle), wherever the head is.
      const polys = this.shieldOutline(hd.playerId);
      if (polys.length > 0) {
        ctx.save();
        ctx.lineWidth = Math.max(1, 0.15 * s);
        ctx.strokeStyle = cssAlpha(0xffffff, 0.85);
        ctx.setLineDash([0.6 * s, 0.4 * s]);
        ctx.lineDashOffset = -((o.nowMs / 1000) * 1.5 * s);
        ctx.beginPath();
        pathPolygons(ctx, polys, ox, oy - lift, s);
        ctx.stroke();
        ctx.restore();
      }
    } else {
      this.shieldRings.delete(hd.playerId);
    }
    if (state?.cashingOut) {
      const r = side * 0.95;
      ctx.lineWidth = Math.max(2, 0.16 * s);
      ctx.strokeStyle = cssAlpha(0xffffff, 0.45);
      ctx.beginPath();
      ctx.arc(x, y - lift, r, 0, TAU);
      ctx.stroke();
      ctx.strokeStyle = css(ARENA_CLIENT.colors.bag);
      ctx.beginPath();
      ctx.arc(
        x,
        y - lift,
        r,
        -Math.PI / 2,
        -Math.PI / 2 + TAU * Math.min(1, Math.max(0, state.progress)),
      );
      ctx.stroke();
    }
  }

  /**
   * A shielded player's land outline (smoothed like the land, so the spawn blob's circle). Its
   * land can only shrink while the shield holds (a capture ends it), so after the first full
   * scan only the last bounds are re-read, and only when a chunk over them changed.
   */
  shieldOutline(id: number): Polygons {
    const st = this.store;
    const prev = this.shieldRings.get(id);
    const key = prev ? chunkKey(st, prev) : 0;
    if (prev && prev.key === key) return prev.polys;
    const x0 = prev ? Math.max(0, prev.x0 - 1) : 0;
    const y0 = prev ? Math.max(0, prev.y0 - 1) : 0;
    const x1 = prev ? Math.min(st.cols - 1, prev.x1 + 1) : st.cols - 1;
    const y1 = prev ? Math.min(st.rows - 1, prev.y1 + 1) : st.rows - 1;
    const cells: number[] = [];
    const ring: ShieldRing = { polys: [], key: 0, x0: st.cols, y0: st.rows, x1: -1, y1: -1 };
    for (let y = y0; y <= y1; y++) {
      const row = y * st.cols;
      for (let x = x0; x <= x1; x++) {
        if (st.owner[row + x] !== id) continue;
        cells.push(row + x);
        if (x < ring.x0) ring.x0 = x;
        if (x > ring.x1) ring.x1 = x;
        if (y < ring.y0) ring.y0 = y;
        if (y > ring.y1) ring.y1 = y;
      }
    }
    if (cells.length > 0) {
      ring.polys = cellSetContours(cells, st.cols);
      ring.key = chunkKey(st, ring);
    } else {
      ring.x0 = x0;
      ring.y0 = y0;
      ring.x1 = x1;
      ring.y1 = y1;
      ring.key = chunkKey(st, ring);
    }
    this.shieldRings.set(id, ring);
    return ring.polys;
  }

  private drawHeadLabels(
    ctx: Ctx2D,
    hd: ArenaDrawHead,
    o: ArenaDrawOptions,
    ox: number,
    oy: number,
    s: number,
    ss: number,
  ): void {
    const st = this.store;
    const k = o.k;
    const x = (ox + hd.x * s) * k;
    const y = (oy + hd.y * s) * k;
    const W = o.view.width * k;
    const H = o.view.height * k;
    if (x < -200 * k || y < -200 * k || x > W + 200 * k || y > H + 200 * k) return;
    const state = st.heads.get(hd.playerId);
    let line = y - 2 * s * k; // name baseline 2 cells above the head centre
    if (!o.hideNames) {
      // An empty nick still gets a name over the head (SF-12), the same one the board shows.
      const name = st.nameOf(hd.playerId) || tr('arena.unnamed');
      this.labels.draw(ctx, name, x, line, 24 * ss * k, ARENA_CLIENT.colors.labelFill, 1);
      line -= 22 * ss * k;
    }
    const value = st.valueOf(hd.playerId);
    const bag = value !== null ? o.formatBag(value) : null;
    if (bag) {
      this.labels.draw(
        ctx,
        bag,
        x,
        line,
        18 * ss * k,
        o.practice ? PRACTICE_SILVER : ARENA_CLIENT.colors.bag,
        1,
      );
      line -= 20 * ss * k;
    }
    if (state?.cashingOut) {
      this.labels.draw(ctx, '$', x, line, 22 * ss * k, ARENA_CLIENT.colors.bag, 1);
      line -= 22 * ss * k;
    }
    if (state?.autopilot || state?.frozen) {
      drawPlug(ctx, x + 20 * ss * k, y - 1.2 * s * k, 9 * ss * k);
    }
    if (o.crownId !== 0 && hd.playerId === o.crownId) {
      drawCrown(ctx, x, line - 6 * ss * k, 28 * ss * k, 18 * ss * k);
    }
  }

  private drawFloating(
    ctx: Ctx2D,
    o: ArenaDrawOptions,
    own: ArenaDrawHead,
    ox: number,
    oy: number,
    s: number,
    ss: number,
  ): void {
    const fx = o.effects;
    if (!fx) return;
    const k = o.k;
    for (const l of fx.labels) {
      const age = o.nowMs - l.bornMs;
      const a = labelAlpha(age, l.lifeMs);
      if (a <= 0) continue;
      const cy = own.y - 5.8 - l.slot * 1.7 + labelDrift(age, l.lifeMs);
      this.labels.draw(
        ctx,
        l.text,
        (ox + own.x * s) * k,
        (oy + cy * s) * k,
        30 * ss * l.size * k,
        l.color,
        1,
        a,
        false,
      );
    }
  }
}

/** Our crown glyph: 3 rounded spikes with ball tips (visuals §6). */
function drawCrown(ctx: Ctx2D, cx: number, bottom: number, w: number, h: number): void {
  const x0 = cx - w / 2;
  ctx.beginPath();
  ctx.moveTo(x0, bottom);
  ctx.lineTo(x0 + w * 0.05, bottom - h * 0.7);
  ctx.lineTo(x0 + w * 0.3, bottom - h * 0.35);
  ctx.lineTo(cx, bottom - h * 0.85);
  ctx.lineTo(x0 + w * 0.7, bottom - h * 0.35);
  ctx.lineTo(x0 + w * 0.95, bottom - h * 0.7);
  ctx.lineTo(x0 + w, bottom);
  ctx.closePath();
  ctx.fillStyle = css(ARENA_CLIENT.colors.crownFill);
  ctx.strokeStyle = css(ARENA_CLIENT.colors.crownStroke);
  ctx.lineWidth = Math.max(1, w / 18);
  ctx.lineJoin = 'round';
  ctx.fill();
  ctx.stroke();
  for (const [px, py] of [
    [x0 + w * 0.05, bottom - h * 0.7],
    [cx, bottom - h * 0.85],
    [x0 + w * 0.95, bottom - h * 0.7],
  ] as const) {
    ctx.beginPath();
    ctx.arc(px, py, w * 0.07, 0, TAU);
    ctx.fill();
    ctx.stroke();
  }
}

/** A small plug glyph (autopilot / frozen). */
function drawPlug(ctx: Ctx2D, x: number, y: number, size: number): void {
  ctx.fillStyle = cssAlpha(0x000000, 0.55);
  ctx.beginPath();
  ctx.arc(x, y, size, 0, TAU);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  const w = size * 0.9;
  ctx.fillRect(x - w / 2, y - size * 0.15, w, size * 0.6);
  ctx.fillRect(x - w * 0.35, y - size * 0.6, size * 0.18, size * 0.45);
  ctx.fillRect(x + w * 0.35 - size * 0.18, y - size * 0.6, size * 0.18, size * 0.45);
  ctx.fillRect(x - size * 0.09, y + size * 0.45, size * 0.18, size * 0.35);
}
